import type {
  EpisodeEvent,
  RecommendationLifecycleEvent,
} from '@troyai/portal/intervention-episodes/data-access';

import type { Visible } from '../demo-data';
import { EPISODE_EVENT_ID_BASE, RECOMMENDATION_EVENT_ID_BASE } from './ids';

/**
 * The activity rail is built from `recommendation_lifecycle`; an episode's own rows point at an
 * entry of `events[]` (which names the actor) by `source_id`. Fixtures list the rows in a compact
 * form and `buildActivity` writes both arrays so the two always agree.
 */
interface RowBase {
  /** Local instant without an offset, e.g. `2026-10-13T09:14:00`. */
  at: string;
  /** `yyyy-MM-dd`: hidden until the demo clock reaches it (the "+3 days" jump). */
  visibleFrom?: string;
}

/** An episode event: `payload` is `{ title, note? }`, the actor an email. */
export interface EpisodeRow extends RowBase {
  kind: 'episode';
  type: string;
  actor: string;
  title: string;
  note?: string;
}

/** A recommendation event; `actor` (an email) goes in `payload.actor`, absent when nobody acted. */
export interface RecommendationRow extends RowBase {
  kind: 'recommendation';
  type: 'generated' | 'accepted';
  actor?: string;
}

export type ActivityRow = EpisodeRow | RecommendationRow;

/** Each episode owns 20 episode-event ids and 4 recommendation-event ids (ids count from 1). */
const episodeIndex = (episodeId: number): number => episodeId % 1000;

export const episodeEventId = (episodeId: number, n: number): number =>
  EPISODE_EVENT_ID_BASE + episodeIndex(episodeId) * 20 + n;

export const recommendationEventId = (episodeId: number, n: number): number =>
  RECOMMENDATION_EVENT_ID_BASE + episodeIndex(episodeId) * 4 + n;

/**
 * The `events` and `recommendation_lifecycle` of one episode. Rows may be listed in any order; the
 * lifecycle comes out oldest first (the page reverses it) and ids count up with time.
 */
export function buildActivity(
  episodeId: number,
  rows: ActivityRow[]
): {
  events: Visible<EpisodeEvent>[];
  recommendation_lifecycle: Visible<RecommendationLifecycleEvent>[];
} {
  const events: Visible<EpisodeEvent>[] = [];
  const lifecycle: Visible<RecommendationLifecycleEvent>[] = [];
  let episodeEvents = 0;
  let recommendationEvents = 0;

  for (const row of [...rows].sort((a, b) => a.at.localeCompare(b.at))) {
    const hidden = row.visibleFrom ? { visibleFrom: row.visibleFrom } : {};
    if (row.kind === 'episode') {
      const eventId = episodeEventId(episodeId, ++episodeEvents);
      // Two copies, so changing one never changes the other.
      const payload = (): { title: string; note?: string } =>
        row.note ? { title: row.title, note: row.note } : { title: row.title };
      events.push({
        event_id: eventId,
        event_type: row.type,
        actor_identity: row.actor,
        payload: payload(),
        occurred_at: row.at,
        ...hidden,
      });
      lifecycle.push({
        source_kind: 'episode_event',
        source_id: eventId,
        event_type: row.type,
        occurred_at: row.at,
        payload: payload(),
        ...hidden,
      });
    } else {
      lifecycle.push({
        source_kind: 'recommendation_event',
        source_id: recommendationEventId(episodeId, ++recommendationEvents),
        event_type: row.type,
        occurred_at: row.at,
        payload: row.actor ? { actor: row.actor } : {},
        ...hidden,
      });
    }
  }
  return { events, recommendation_lifecycle: lifecycle };
}
